import { useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import { ApiError, api } from '../api/client';
import { Alert, Field } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import { ANGREFRIST_DAGER } from '../lib/angrerett';
import { SELSKAP } from '../lib/selskap';
import { SIDETITLER, useSidehode } from '../lib/sidehode';

/**
 * Angreskjemaet, til utfylling.
 *
 * Loven krever at det standardiserte skjemaet *finnes* og at kunden får det –
 * ikke at det fylles ut for hånd og sendes i posten. Derfor ligger det her, som
 * en side man fyller ut og sender, og adressen står i ordrebekreftelsen.
 *
 * **Åpen side, uten innlogging.** Angreretten tilhører kunden, ikke kontoen.
 * Den som har glemt passordet eller alt har slettet kontoen, skal fortsatt kunne
 * gå fra kjøpet. Er man logget inn, fylles navn og e-post ut på forhånd – resten
 * er kundens egne ord, og skal ikke gjettes av oss.
 *
 * **En e-post holder også.** Det står her, i klartekst. Et skjema som lar som
 * det er den eneste veien, gjør en rettighet trangere enn loven gjorde den.
 */
export function AngrerettPage() {
  const { user } = useAuth();
  useSidehode(SIDETITLER.angrerett, '/vilkaar/angrerett');

  const [hva, setHva] = useState('');
  const [kvitteringsnummer, setKvitteringsnummer] = useState('');
  const [bestiltDato, setBestiltDato] = useState('');
  const [navn, setNavn] = useState(user?.name ?? '');
  const [epost, setEpost] = useState(user?.email ?? '');
  const [adresse, setAdresse] = useState('');
  const [bekreftet, setBekreftet] = useState(false);

  const [sender, setSender] = useState(false);
  const [feil, setFeil] = useState<string | null>(null);
  const [kvittert, setKvittert] = useState<{ sendtDato: string; kopiTil: string } | null>(null);

  const send = async (event: FormEvent) => {
    event.preventDefault();
    setSender(true);
    setFeil(null);
    try {
      setKvittert(
        await api.post<{ sendtDato: string; kopiTil: string }>('/angrerett', {
          hva: hva.trim(),
          kvitteringsnummer: kvitteringsnummer.trim() || undefined,
          bestiltDato: bestiltDato.trim() || undefined,
          navn: navn.trim(),
          epost: epost.trim(),
          adresse: adresse.trim() || undefined,
          bekreftet,
        }),
      );
    } catch (caught) {
      setFeil(
        caught instanceof ApiError
          ? caught.message
          : `Klarte ikke å sende meldingen. Send en e-post til ${SELSKAP.epost} i stedet, så gjelder den fra i dag.`,
      );
    } finally {
      setSender(false);
    }
  };

  if (kvittert) {
    return (
      <div className="legal-page">
        <Link to="/" className="small">
          ← Til forsiden
        </Link>
        <h1>Meldingen er sendt</h1>
        <Alert kind="success">
          Vi har fått beskjed om at du vil gå fra kjøpet. Meldingen er datert{' '}
          {kvittert.sendtDato}.
        </Alert>
        <p>
          Kopien er sendt til <strong>{kvittert.kopiTil}</strong>. Ta vare på den – datoen i den
          er den som viser at du var innenfor fristen.
        </p>
        <p>
          Vi betaler tilbake <strong>hele beløpet</strong> til den samme betalingsmåten du brukte,
          uten unødig opphold og senest fjorten dager etter at vi mottok meldingen. Tilgangen til
          den betalte pakken faller bort; gjestelista og RSVP er gratis og blir stående.
        </p>
        <p className="small muted">
          Kom det ingen kopi på e-post i løpet av noen minutter, si fra til{' '}
          <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a>. Meldingen er registrert hos oss
          uansett, men du skal ha ditt eget bevis.
        </p>
      </div>
    );
  }

  return (
    <div className="legal-page">
      <Link to="/" className="small">
        ← Til forsiden
      </Link>
      <h1>Angreskjema</h1>
      <p className="muted">
        Fyll ut og send dette skjemaet bare dersom du ønsker å gå fra avtalen.
      </p>

      <p>
        Du har <strong>{ANGREFRIST_DAGER} dagers angrerett</strong>, regnet fra dagen etter at
        kjøpet ble gjort, og den gjelder selv om dere har tatt tjenesten i bruk. Den nøyaktige
        datoen står i ordrebekreftelsen du fikk på e-post.
      </p>
      <p>
        <strong>Skjemaet er et tilbud, ikke et krav.</strong> Det holder å sende en e-post til{' '}
        <a href={`mailto:${SELSKAP.epost}`}>{SELSKAP.epost}</a> der det går klart fram at du vil gå
        fra avtalen. Vilkårene for kjøpet står i{' '}
        <Link to="/salgsbetingelser">salgsbetingelsene</Link>.
      </p>

      {feil ? <Alert>{feil}</Alert> : null}

      <form onSubmit={(event) => void send(event)} className="angreskjema-form">
        <h2>Kjøpet</h2>
        <Field label="Hva du vil gå fra" hint="For eksempel «Vår Dag Plus».">
          <input
            className="input"
            required
            maxLength={300}
            value={hva}
            onChange={(event) => setHva(event.target.value)}
          />
        </Field>
        <div className="form-grid">
          <Field label="Kvitteringsnummer" hint="Står på kvitteringen, som «VD-00001». Valgfritt.">
            <input
              className="input"
              maxLength={40}
              value={kvitteringsnummer}
              onChange={(event) => setKvitteringsnummer(event.target.value)}
            />
          </Field>
          <Field label="Bestilt dato" hint="Valgfritt.">
            <input
              className="input"
              type="date"
              value={bestiltDato}
              onChange={(event) => setBestiltDato(event.target.value)}
            />
          </Field>
        </div>

        <h2>Deg</h2>
        <Field label="Navn">
          <input
            className="input"
            required
            maxLength={120}
            value={navn}
            onChange={(event) => setNavn(event.target.value)}
          />
        </Field>
        <Field label="E-postadresse" hint="Hit sender vi kopien, og hit svarer vi.">
          <input
            className="input"
            type="email"
            required
            value={epost}
            onChange={(event) => setEpost(event.target.value)}
          />
        </Field>
        <Field label="Adresse" hint="Valgfritt. Pengene går tilbake til betalingsmåten du brukte.">
          <input
            className="input"
            maxLength={200}
            value={adresse}
            onChange={(event) => setAdresse(event.target.value)}
          />
        </Field>

        {/*
          Avkryssingen er ikke en formalitet – det er den som *er* meldingen om
          å gå fra avtalen. Uten den har vi et utfylt skjema, og et utfylt
          skjema er ikke et varsel. Derfor står den for seg, rett over knappen.
        */}
        <label className="checkbox angreerklaering">
          <input
            type="checkbox"
            checked={bekreftet}
            onChange={(event) => setBekreftet(event.target.checked)}
          />
          <span>
            Jeg underretter herved om at jeg ønsker å gå fra min avtale om kjøpet over.
          </span>
        </label>

        <button className="btn btn-primary" type="submit" disabled={sender || !bekreftet}>
          {sender ? 'Sender …' : 'Send meldingen'}
        </button>
        <p className="small muted">
          Du får en kopi på e-post med det samme. Når du angrer, faller tilgangen til den betalte
          pakken bort; gjestelista og RSVP er gratis og blir stående.
        </p>
      </form>
    </div>
  );
}
